import type { TowerDesign } from "@/game/towerSpec";

/**
 * 40 m carrier monopole — a taller, heavier variant of the base design for
 * multi-operator antenna loading. Same "slim braced lattice clad in a tapered
 * tube" model (see docs/ENGINEERING.md §3.3), scaled up for the longer moment
 * arm of 40 m of wind and top-mounted antennas.
 *
 * Sizing rationale:
 *  - faces: 8 — rounder + stiffer polygon; base leg couple spread over 8 legs.
 *  - baseRadius: 0.95 m (base width 1.9 m, aspect ~21:1) — a real 40 m carrier
 *    pole has a ~0.9–1.0 m base plate. The wider base gives the lateral bending
 *    stiffness a 40 m slender pole needs so it settles at ~0% util (no sway
 *    spike) rather than sitting marginal like the old 0.6 m design.
 *  - legSection: legXXL (8" Sch-40 pipe), 14 panels → 2.86 m unbraced length:
 *    legs YIELD before buckling (Pcr ≫ A·Fy), so failure is base bending with
 *    the windward leg at A·Fy = 1870 kN → base-moment capacity ≈ 7.1 MN·m.
 *  - angleL5 diagonals — heavy enough to stay well below buckling over the
 *    slightly longer panels and carry the shear from 40 m of wind.
 * Result: peak util ≈ 0 at zero wind, ~0.13 at 30 m/s, ~0.30 at 45 m/s, ~0.54
 * at 60 m/s, reaching the base-bending limit near ~75 m/s — comfortable
 * operational headroom, plausible base-overturn failure in a storm.
 *
 * shell: baseDiameter 1.9 m = 2×baseRadius encloses the lattice legs; tapers to
 * 1.0 m at the top.
 */
export const design: TowerDesign = {
  id: "monopole-40m-carrier",
  name: "Monopole 40 m (Carrier)",
  description: "40 m heavy carrier monopole for multi-operator antenna loading.",
  spec: {
    type: "monopole",
    height: 40,
    stack: {
      faces: 8,
      panels: 14,
      baseRadius: 0.95,
      topRadius: 0.5,
      legSection: "legXXL",
      braceSection: "angleL5",
      bracing: "X",
    },
    shell: {
      baseDiameter: 1.9,
      topDiameter: 1.0,
    },
  },
};
